import React, { useContext } from "react";
import { OrderDetailItem, OrderSplitResultItem } from "../../interfaces/interfaces";
import { Context, IContext } from "../../context/ContextApp";

interface Props {
  items: OrderSplitResultItem[] | OrderDetailItem[];
  showStock?: boolean;
  bo?: boolean;
  disableAvailableQty?: boolean;
}

const ProductListCards: React.FC<Props> = ({
  items,
  showStock,
  bo,
  disableAvailableQty = false,
}) => {
  const { appBranch }: IContext = useContext(Context);

  if (!items?.length) {
    return (
      <div className="product-list-empty">
        <p>No products found</p>
      </div>
    );
  }

  // Helper functions
  const getImageUrl = (item: any) => {
    const frontImage = item.images?.find((img: any) => img.front || img.front_image);
    return (
      frontImage?.https_attachment_url_product ||
      item.images?.[0]?.https_attachment_url_product ||
      "https://cdn.fashionbiz.com/Image+Not+Found.jpg"
    );
  };

  const getRegionStock = (item: any, region: string) => {
    const warehouse = item.warehouses?.find((w: any) => w.location === region);
    return warehouse ? parseInt(warehouse.quantity) : 0;
  };

  const formatPrice = (price: any) => `$${parseFloat(price || "0").toFixed(2)}`;

  const isInStock = (item: any) => (item.available_quantity || 0) > 0;

  return (
    <div className="product-list-cards-container">
      {items.map((product, index) => (
        <div key={index} className="product-card">
          <div className="product-header">
            <div className="product-identity">
              <div className="product-image-container">
                <img
                  src={getImageUrl(product)}
                  alt={product.product_name || "Product"}
                  className="product-image"
                />
              </div>
              <div className="product-info">
                <h3 className="product-name">{product.product_name}</h3>
                <div className="product-meta">
                  <span className="product-code">
                    {(product as any).product_code || (product as any).itemNo}
                  </span>
                  <span className="product-sku">SKU {product.sku}</span>
                </div>
              </div>
            </div>
            <div className="product-summary">
              <div className="summary-row">
                <div className="summary-item">
                  <span className="summary-label">Available Qty</span>
                  <span className="summary-value highlight">
                    {(product as any).available_quantity || 0}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Total</span>
                  <span className="summary-value total">
                    {formatPrice((product as any).available_price)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="product-details">
            <table className="detail-table">
              <thead>
                <tr>
                  <th>Colour</th>
                  <th>Size</th>
                  {items.some((i) => i.reference && i.reference !== "-") && <th>Reference</th>}
                  <th>Unit Price</th>
                  {["au", "nz"].includes(appBranch) && !disableAvailableQty && <th>Order Qty</th>}
                  {appBranch === "au" && <th>SYD</th>}
                  {appBranch === "au" && <th>MEL</th>}
                  {appBranch === "au" && <th>PER</th>}
                  {(bo || showStock) && <th>Status</th>}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td data-label="Colour">{product.color || "—"}</td>
                  <td data-label="Size">{product.size || "—"}</td>
                  {items.some((i) => i.reference && i.reference !== "-") && (
                    <td data-label="Reference">
                      {product.reference && product.reference !== "-" ? product.reference : ""}
                    </td>
                  )}
                  <td data-label="Unit Price">{formatPrice(product.price)}</td>
                  {["au", "nz"].includes(appBranch) && !disableAvailableQty && (
                    <td data-label="Order Qty">{product.quantity || 0}</td>
                  )}
                  {appBranch === "au" && <td data-label="SYD">{getRegionStock(product, "SYD")}</td>}
                  {appBranch === "au" && <td data-label="MEL">{getRegionStock(product, "MEL")}</td>}
                  {appBranch === "au" && <td data-label="PER">{getRegionStock(product, "PER")}</td>}
                  {(bo || showStock) && (
                    <td data-label="Status">
                      {bo && (
                        <div
                          className={`backorder-status ${(product as any).backorder ? "backorder" : "in-stock"}`}
                        >
                          {(product as any).backorder ? (
                            <>
                              <span className="backorder-quantity">
                                {(product as any).backorder.quantity}
                              </span>
                              <span className="backorder-eta">
                                {(product as any).eta && (product as any).eta !== "Pending"
                                  ? JSON.parse((product as any).eta.replaceAll("=>", ":"))[0]
                                      ?.eta || "Pending"
                                  : "Pending"}
                              </span>
                            </>
                          ) : (
                            <span className="in-stock-text">In Stock</span>
                          )}
                        </div>
                      )}
                      {showStock && (
                        <div
                          className={`stock-indicator ${isInStock(product) ? "in-stock" : "out-of-stock"}`}
                        >
                          <span className="stock-dot" />
                          <span className="stock-text">
                            {isInStock(product) ? "In Stock" : "Out of Stock"}
                          </span>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProductListCards;
